/**
 * Shared Chart.js scale and tooltip config for analytics line/bar charts.
 * Use these to keep axis styling consistent (color #6B7280, Inter 11px, autoSkip: false for x).
 */

export const ANALYTICS_CHART_TICK_COLOR = '#6B7280';
export const ANALYTICS_CHART_TICK_FONT = { size: 11, family: 'Inter, sans-serif' as const };

/** Shared arc style for analytics donut charts (service revenue, customer breakdown). */
export const ANALYTICS_DONUT_ARC_ELEMENTS = {
  arc: {
    hoverOffset: 0,
    hoverBorderWidth: 0
  }
};

/** Skip hover/active tween so Chart.js does not leave ghost strokes on the canvas. */
export const ANALYTICS_CHART_NO_HOVER_ANIMATION = {
  transitions: {
    active: {
      animation: {
        duration: 0
      }
    }
  }
} as const;

/** Shared layout for every analytics Chart.js options object. */
export const ANALYTICS_RESPONSIVE_CHART_BASE = {
  ...ANALYTICS_CHART_NO_HOVER_ANIMATION,
  responsive: true,
  maintainAspectRatio: false,
  resizeDelay: 0
};

/** Shared hover/tooltip interaction for analytics line charts (revenue, growth, retention). */
export const ANALYTICS_LINE_CHART_INTERACTION = {
  intersect: false,
  mode: 'index' as const
};

/** Shared donut layout for service revenue and customer breakdown charts. */
export const ANALYTICS_DONUT_CHART_BASE = {
  ...ANALYTICS_RESPONSIVE_CHART_BASE,
  cutout: '60%',
  elements: ANALYTICS_DONUT_ARC_ELEMENTS
};

/** X scale: no grid, gray ticks, show all labels (autoSkip: false) */
export const ANALYTICS_CHART_SCALE_X = {
  display: true,
  grid: { display: false },
  ticks: {
    color: ANALYTICS_CHART_TICK_COLOR,
    font: ANALYTICS_CHART_TICK_FONT,
    autoSkip: false
  }
} as const;

/** Y scale base: beginAtZero, light grid, gray ticks. Spread and add maxTicksLimit/callback as needed. */
export const ANALYTICS_CHART_SCALE_Y_BASE = {
  display: true,
  beginAtZero: true,
  grid: {
    color: 'rgba(107, 114, 128, 0.1)',
    drawBorder: false
  },
  ticks: {
    color: ANALYTICS_CHART_TICK_COLOR,
    font: ANALYTICS_CHART_TICK_FONT
  }
} as const;

/** Simple tooltip label: "datasetLabel: value". Use in tooltip.callbacks.label. */
export function getAnalyticsSimpleDatasetLabel(context: { dataset: { label: string }; parsed: { y: number } }): string {
  return `${context.dataset.label}: ${context.parsed.y}`;
}

/** Shared tick style used by the bar and line chart option builders below. */
const CHART_TICK_STYLE = {
  color: '#555963',
  font: { family: 'Inter', size: 12, weight: '500' as const },
  maxTicksLimit: 6
};

/** Shared x-axis scale used by the bar and line chart option builders below. */
const CHART_X_SCALE = {
  display: true,
  grid: { display: false },
  ticks: CHART_TICK_STYLE
};

/**
 * Shared bar chart options for analytics (Popular Services, Employee Performance, Employee Hours).
 * Only the tooltip unit label differs (e.g. 'services', 'points', 'hours').
 */
export function createAnalyticsBarChartOptions(unitLabel: string): object {
  return {
    ...ANALYTICS_RESPONSIVE_CHART_BASE,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: false,
        callbacks: {
          label: (context: { parsed: { y: number } }) => `${context.parsed.y} ${unitLabel}`
        }
      }
    },
    scales: {
      x: CHART_X_SCALE,
      y: {
        display: true,
        beginAtZero: true,
        grid: { display: false },
        ticks: CHART_TICK_STYLE
      }
    },
    elements: {
      bar: {
        borderRadius: 4,
        borderSkipped: false
      }
    },
    categoryPercentage: 0.8,
    barPercentage: 0.9
  };
}

/**
 * Line chart options with legend at bottom and tooltip showing value + percentage change from previous point.
 * Used for service trends chart (unitLabel e.g. 'services').
 */
export function createAnalyticsLineChartOptionsWithPercentChange(unitLabel: string): object {
  return {
    ...ANALYTICS_RESPONSIVE_CHART_BASE,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          color: '#717683',
          font: { family: 'Inter', size: 12, weight: '500' }
        }
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        borderColor: '#E5E7EB',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
        callbacks: {
          title: (context: any) => `Time: ${context[0].label}`,
          label: (context: any) => {
            const currentValue = context.parsed.y;
            const previousValue = context.dataset.data[context.dataIndex - 1];
            let percentageChange = '';
            if (previousValue !== undefined && previousValue > 0) {
              const change = ((currentValue - previousValue) / previousValue) * 100;
              percentageChange = ` (${change >= 0 ? '+' : ''}${change.toFixed(1)}%)`;
            }
            return `${context.dataset.label}: ${currentValue} ${unitLabel}${percentageChange}`;
          }
        }
      }
    },
    scales: {
      x: CHART_X_SCALE,
      y: {
        display: true,
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 26, 0.15)', drawBorder: false },
        ticks: CHART_TICK_STYLE
      }
    },
    elements: {
      point: { radius: 4, hoverRadius: 8, borderWidth: 2, borderColor: '#ffffff' },
      line: { borderWidth: 2 }
    }
  };
}
